/**
 * Pure report arithmetic. The screen, the totals row, the balance badge and
 * the exported file all read these functions, so a figure cannot be right in
 * one place and wrong in another (spec §7.7–§7.8).
 */

import type { Language } from '../../i18n/catalogs';
import { compareDecimal, decimalToUnits, formatMoney, sumDecimal, unitsToDecimal } from '../../components/data-grid/money';
import type {
  AccountAnalysisGroup,
  AccountAnalysisReport,
  AccountCardReport,
  ChessTableReport,
  SideTotals,
  TrialBalanceReport,
  TrialBalanceRow,
} from './reports.api';
import { reportMonth } from './periods';

const SIDE_KEYS = [
  'openingDt',
  'openingCt',
  'turnoverDt',
  'turnoverCt',
  'closingDt',
  'closingCt',
] as const;

export interface BalanceCheck {
  balanced: boolean;
  debit: string;
  credit: string;
  difference: string;
}

/** Sums the leaf rows only: a group row already carries its children. */
export function sumLeafSideTotals(rows: readonly TrialBalanceRow[]): SideTotals {
  const leaves = rows.filter((row) => !row.isGroup);
  const totals = {} as SideTotals;
  for (const key of SIDE_KEYS) {
    totals[key] = sumDecimal(leaves.map((row) => row[key] ?? '0'));
  }
  return totals;
}

/** Σ Dt against Σ Kt over the period turnover, with the difference spelled out. */
export function checkBalance(totals: SideTotals, language: Language): BalanceCheck {
  const debit = decimalToUnits(totals.turnoverDt, 4);
  const credit = decimalToUnits(totals.turnoverCt, 4);
  const difference = debit > credit ? debit - credit : credit - debit;
  return {
    balanced: difference === 0n,
    debit: formatMoney(totals.turnoverDt, language),
    credit: formatMoney(totals.turnoverCt, language),
    difference: formatMoney(unitsToDecimal(difference, 4), language),
  };
}

/** The account card's running balance must land on the trial-balance closing row. */
export function accountCardMatchesTrial(card: AccountCardReport, trial: TrialBalanceReport): boolean {
  const row = trial.rows.find((candidate) => !candidate.isGroup && candidate.accountCode === card.account);
  if (row === undefined) {
    return false;
  }
  const lastBalance = card.rows[card.rows.length - 1]?.balance;
  return (
    compareDecimal(card.closing, row.closing) === 0 &&
    (lastBalance === undefined || compareDecimal(lastBalance, row.closing) === 0)
  );
}

/** Every row is a debit account and every column a credit account (spec §7.8). */
export function chessMatchesTrial(chess: ChessTableReport, trial: TrialBalanceReport): boolean {
  const leaves = new Map(trial.rows.filter((row) => !row.isGroup).map((row) => [row.accountCode, row]));
  let rowSum = '0.0000';
  for (const row of chess.rows) {
    const account = leaves.get(row.accountCode);
    if (account === undefined || compareDecimal(row.total, account.turnoverDt) !== 0) {
      return false;
    }
    rowSum = sumDecimal([rowSum, row.total]);
  }
  let columnSum = '0.0000';
  for (const column of chess.columns) {
    const account = leaves.get(column.accountCode);
    if (account === undefined || compareDecimal(column.total, account.turnoverCt) !== 0) {
      return false;
    }
    columnSum = sumDecimal([columnSum, column.total]);
  }
  return (
    compareDecimal(rowSum, columnSum) === 0 &&
    compareDecimal(rowSum, chess.grandTotal) === 0 &&
    compareDecimal(rowSum, trial.totals.turnoverDt) === 0
  );
}

/** A group's share of the report turnover, in percent with one decimal. */
export function groupShare(group: AccountAnalysisGroup, totals: AccountAnalysisReport['totals']): number {
  const groupTurnover = decimalToUnits(group.turnoverDt, 4) + decimalToUnits(group.turnoverCt, 4);
  const totalTurnover = decimalToUnits(totals.turnoverDt, 4) + decimalToUnits(totals.turnoverCt, 4);
  if (totalTurnover === 0n) {
    return 0;
  }
  // Round half up: four groups that each round down would otherwise sum below 100%.
  const perMille = (groupTurnover * 1000n + totalTurnover / 2n) / totalTurnover;
  return Number(perMille) / 10;
}

export function formatPercent(value: number, language: Language): string {
  const fixed = value.toFixed(1);
  return language === 'en' ? `${fixed}%` : `${fixed.replace('.', ',')}%`;
}

/** `trial-balance-2026-11`, used as both the sheet name and the file stem. */
export function reportSheetName(kind: string, report: { to: string }): string {
  const month = reportMonth(report.to);
  return month === null ? kind : `${kind}-${month.year}-${String(month.month).padStart(2, '0')}`;
}

export function trialBalanceSheetName(report: TrialBalanceReport): string {
  return reportSheetName('trial-balance', report);
}
